import PassengerAddress from "../../../common/types/passenger-address";
import PassengerDispatchSelectOption from "../../../common/types/passenger-dispatch-select-option";
import PassengerEditFormData from "../types/passenger-edit-form-data";
import PassengerUpdateParams, {
  PassengerUpdateParamsAddress,
} from "../../../../../../common/services/passenger/update/passenger-update-params";
import PassengerData, {
  PassengerDataPassengerAddress,
  PassengerDataPassengerDispatch,
} from "../../../../../../common/services/passenger/data/passenger-data";

const createPassengerAddress = (
  passengerRequestAddress: PassengerDataPassengerAddress,
): PassengerAddress => {
  return {
    latitude: passengerRequestAddress.lat,
    longitude: passengerRequestAddress.lon,
    country: passengerRequestAddress.country,
    town: passengerRequestAddress.town,
    zipCode: passengerRequestAddress.zipCode,
    street: passengerRequestAddress.street,
    houseNumber: passengerRequestAddress.houseNo,
    apartmentNumber: passengerRequestAddress.apartment,
    description: passengerRequestAddress.description,
  };
};

const createEditPassengerRequestAddress = (
  formDataAddress: PassengerAddress,
): PassengerUpdateParamsAddress => {
  return {
    lat: formDataAddress.latitude,
    lon: formDataAddress.longitude,
    country: formDataAddress.country?.trim() || null,
    town: formDataAddress.town?.trim() || null,
    street: formDataAddress.street?.trim() || null,
    zipCode: formDataAddress.zipCode?.trim() || null,
    houseNo: formDataAddress.houseNumber?.trim() || null,
    apartment: formDataAddress.apartmentNumber?.trim() || null,
    description: formDataAddress.description?.trim() || null,
  };
};

const createEditPassengerRequestAddresses = (
  formDataAddresses: PassengerAddress[],
): PassengerUpdateParamsAddress[] => {
  return formDataAddresses.map((address: PassengerAddress) => {
    return createEditPassengerRequestAddress(address);
  });
};

const createEditPassengerRequestDispatchIds = (
  formDataDispatches: PassengerDispatchSelectOption[],
): string[] => {
  return formDataDispatches.map((dispatch) => {
    return dispatch.value.uuid;
  });
};

const createEditPassengerRequest = (
  formData: PassengerEditFormData,
  passengerUuid: string,
) => {
  const addresses = createEditPassengerRequestAddresses(formData.addresses);
  const dispatchIds = createEditPassengerRequestDispatchIds(
    formData.dispatches,
  );

  const request: PassengerUpdateParams = {
    passengerUuid,
    email: formData.email?.trim() || null,
    firstName: formData.firstName?.trim() || null,
    lastName: formData.lastName?.trim() || null,
    mobilePrimary: formData.mobile.number?.trim() || null,
    mobilePrimaryPrefix: formData.mobile.dialingCode || null,
    mobileSecondary: formData.alternativeMobile.number?.trim() || null,
    mobileSecondaryPrefix: formData.alternativeMobile.dialingCode || null,
    passenger: {
      addresses: addresses,
      dispatchIds: dispatchIds,
    },
  };

  return request;
};

const createDispatchSelectOption = (
  responseDispatch: PassengerDataPassengerDispatch,
): PassengerDispatchSelectOption => {
  return {
    label: responseDispatch.displayName,
    value: {
      uuid: responseDispatch.id,
      displayName: responseDispatch.displayName,
      addressString: responseDispatch.addressString,
      cargoCompanyId: "",
    },
  };
};

const createDispatchSelectOptions = (
  responseDispatches: PassengerDataPassengerDispatch[],
): PassengerDispatchSelectOption[] => {
  return responseDispatches.map(createDispatchSelectOption);
};

const createFormData = (responseData: PassengerData): PassengerEditFormData => {
  return {
    email: responseData.email,
    firstName: responseData.firstName,
    lastName: responseData.lastName,
    mobile: {
      dialingCode: responseData.mobilePrimaryPrefix,
      number: responseData.mobilePrimary,
    },
    alternativeMobile: {
      dialingCode: responseData.mobileSecondaryPrefix,
      number: responseData.mobileSecondary,
    },
    addresses: responseData.passenger.addresses.map((responseDataAddress) =>
      createPassengerAddress(responseDataAddress),
    ),
    dispatches: responseData.passenger.dispatches.map((dispatch) =>
      createDispatchSelectOption(dispatch),
    ),
  };
};

const passengerEditFactory = {
  createEditPassengerRequest,
  createFormData,
  createDispatchSelectOptions,
};

export default passengerEditFactory;
